import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api } from "../../lib/api";

const statusOptions = ["active", "inactive"];

export default function AdminStaffList() {
  const [staff, setStaff] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (status) params.set("status", status);
    try {
      const data = await api(`/api/admin/staff${params.toString() ? `?${params}` : ""}`);
      setStaff(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const stats = useMemo(() => staff.reduce((acc, person) => {
    acc.total += 1;
    acc[person.status] = (acc[person.status] || 0) + 1;
    return acc;
  }, { total: 0, active: 0, inactive: 0 }), [staff]);

  async function updateStatus(id, nextStatus) {
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/staff/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      setMessage("Staff status updated.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteStaff(id) {
    if (!window.confirm("Delete this staff profile permanently?")) return;
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/staff/${id}`, { method: "DELETE" });
      setMessage("Staff profile deleted.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <AdminLayout
      title="Staff Directory CMS"
      subtitle="Manage lecturer, HOD, director, unit and administrative staff profiles."
      actions={<Link to="/adxm/staff/create" className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-bold text-white">Add Staff</Link>}
    >
      <div className="grid gap-4 md:grid-cols-3">
        {[["Total", stats.total], ["Active", stats.active], ["Inactive", stats.inactive]].map(([label, value]) => (
          <div key={label} className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <p className="text-sm font-bold text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-black text-slate-950">{value || 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="grid gap-3 md:grid-cols-[1fr_220px_120px]">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name, department, position, email or research..."
            className="rounded-xl border border-slate-300 px-4 py-3"
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 px-4 py-3">
            <option value="">All status</option>
            {statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <button onClick={load} className="rounded-xl bg-slate-950 px-4 py-3 font-bold text-white">Search</button>
        </div>
      </div>

      {message && <div className="mt-6 rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-700">{message}</div>}
      {error && <div className="mt-6 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

      <div className="mt-6 overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="p-4">Staff</th>
                <th className="p-4">Department</th>
                <th className="p-4">Position</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Status</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500">Loading staff...</td></tr>
              ) : staff.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-slate-500">No staff profiles found.</td></tr>
              ) : staff.map((person) => (
                <tr key={person.id} className="border-t border-slate-100 align-top">
                  <td className="p-4">
                    <div className="flex gap-3">
                      {person.photo_url ? <img src={person.photo_url} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="h-14 w-14 rounded-xl bg-slate-100" />}
                      <div>
                        <p className="font-black text-slate-900">{person.title ? `${person.title} ` : ""}{person.full_name}</p>
                        <p className="text-xs text-slate-500">{person.office || "No office saved"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">{person.department_name || "—"}</td>
                  <td className="p-4">{person.position || "—"}</td>
                  <td className="p-4">
                    {person.email && <a href={`mailto:${person.email}`} className="block font-bold text-emerald-700">{person.email}</a>}
                    {person.phone && <span className="text-xs text-slate-500">{person.phone}</span>}
                  </td>
                  <td className="p-4">
                    <select value={person.status || "inactive"} onChange={(e) => updateStatus(person.id, e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold">
                      {statusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-2">
                      {person.slug && <Link to={`/staff/${person.slug}`} target="_blank" className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700">View</Link>}
                      <Link to={`/adxm/staff/edit/${person.id}`} className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700">Edit</Link>
                      <button onClick={() => deleteStaff(person.id)} className="rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
